import type {
  KiliDropdownFilter,
  KiliIdFilter,
  KiliPathFilter,
  KiliStringFilter,
} from "@/types/kili/kili-filters.type.ts";
import type { ZodRawShape } from "zod";

import { z } from "zod";

function atLeastOne<T>(arr: T[]): [T, ...T[]] {
  if (arr.length < 1) {
    throw new Error("Instantiated with fewer than one item");
  }

  return arr as [T, ...T[]];
}

export interface IDataTableDropdownOption {
  label: string;
  value: any;
}

export type TKiliDataTableDefinition<SCHEMA extends z.ZodObject<any>> =
  SCHEMA extends z.ZodObject<infer SCHEMA_DEFINITION>
    ? SCHEMA_DEFINITION extends z.ZodRawShape
      ? {
          schema: z.ZodObject<SCHEMA_DEFINITION>;
          headers: {
            [K in keyof Partial<SCHEMA_DEFINITION>]: {
              label: string;
            };
          };
          filterableFields: {
            [K in keyof Partial<SCHEMA_DEFINITION>]: {
              key: string;
              filter:
                | {
                    filterType: typeof KiliIdFilter | typeof KiliStringFilter | typeof KiliPathFilter;
                    options?: any;
                  }
                | {
                    filterType: typeof KiliDropdownFilter;
                    options:
                      | Array<IDataTableDropdownOption>
                      | (() => Array<IDataTableDropdownOption>)
                      | (() => Promise<Array<IDataTableDropdownOption>>);
                  };
              customLabel?: string;
            };
          };
          search: (body: TKiliDataTableSearchRequestBody) => Promise<TKiliDataTableRequestResponse<SCHEMA>>;
          searchQueryKey: (param?: any) => any;
        }
      : never
    : never;

export enum EKiliInvalidFilterErrorCode {
  UNKNOWN_COLUMN = "UNKNOWN_COLUMN",
  INVALID_VALUE = "INVALID_VALUE",
  UNEXPECTED_FILTER_ERROR = "UNEXPECTED_FILTER_ERROR",
}

export interface TKiliDataTableFilter {
  name: string;
  value: any;
  operator: any;
}

export interface TKiliDataTableExportRequestBody {
  filters: Array<TKiliDataTableFilter>;
}

export type TKiliDataTableRequestParams = Record<string, number>;

export type TKiliDataTableSearchRequestBody = TKiliDataTableExportRequestBody & {
  pageSize: number;
  page: number;
  params?: TKiliDataTableRequestParams;
};

export type TKiliDataTableRequestResponse<SCHEMA> =
  SCHEMA extends z.ZodObject<any>
    ? {
        rows: Array<z.infer<SCHEMA>>;
        totalRows: number;
        invalidFilters: Array<{
          name: string;
          message: string;
          code: EKiliInvalidFilterErrorCode;
        }>;
      }
    : never;

export function kiliDataTableSearchRequestBody<T extends TKiliDataTableDefinition<any>>(
  dataTableFilterableFields: T["filterableFields"],
) {
  return kiliDataTableExportRequestBody(dataTableFilterableFields).extend({
    pageSize: z.number(),
    page: z.number(),
  });
}

export function kiliDataTableExportRequestBody<T extends TKiliDataTableDefinition<any>>(
  filterableFields: T["filterableFields"],
) {
  const filters = z.array(
    z.discriminatedUnion("name", [
      ...atLeastOne(
        Object.values(filterableFields).map((field) => {
          return z.object({
            name: z.literal(field.key),
            value: z.any(),
            operator: z.string(),
          });
        }),
      ),
    ]),
  );

  return z.object({
    filters,
  });
}

export function kiliDataTableRequestResponse<T extends ZodRawShape>(schema: z.ZodObject<T>) {
  return z.object({
    rows: z.array(schema),
    totalRows: z.number(),
    invalidFilters: z.array(
      z.object({
        name: z.string(),
        message: z.string(),
        code: z.nativeEnum(EKiliInvalidFilterErrorCode),
      }),
    ),
  });
}
